import {
  AlertCircle,
  CheckCircle2,
  Clock,
  Loader2,
  RefreshCw,
  Upload,
  X,
  XCircle,
} from "lucide-react";
import React, { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { adminService, BasicLoanUploadStatus } from "../adminApi";

const QUERY_KEY = ["basic-loan-upload-status"];

const TaskStatusBadge: React.FC<{
  status: BasicLoanUploadStatus["last_task_status"];
}> = ({ status }) => {
  if (!status) return <span className="text-sm text-gray-400">—</span>;
  if (status === "STARTED") {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-yellow-100 px-2.5 py-0.5 text-xs font-medium text-yellow-800">
        <Loader2 className="h-3 w-3 animate-spin" />
        Processing…
      </span>
    );
  }
  if (status === "SUCCESS") {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
        <CheckCircle2 className="h-3 w-3" />
        Success
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-800">
      <XCircle className="h-3 w-3" />
      Failed
    </span>
  );
};

const UploadDailyCollection: React.FC = () => {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadError, setUploadError] = useState("");
  const [isUploading, setIsUploading] = useState(false);

  const { data, isFetching } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: async () => {
      const response = await adminService.getBasicLoanUploadStatus();
      return response.data;
    },
  });

  const uploadMutation = useMutation({
    mutationFn: ({ file }: { file: File }) =>
      adminService.uploadBasicLoanDetails(file),
    onSuccess: () => {
      setIsUploading(false);
      setUploadError("");
      if (fileInputRef.current) fileInputRef.current.value = "";
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
    onError: (err: any) => {
      setIsUploading(false);
      const msg =
        err?.response?.data?.error ??
        err?.message ??
        "Upload failed. Please try again.";
      setUploadError(msg);
    },
  });

  const handleUpload = () => {
    const file = fileInputRef.current?.files?.[0] ?? null;
    if (!file) {
      setIsUploading(false);
      setUploadError("Please select a CSV file.");
      return;
    }
    setUploadError("");
    setIsUploading(true);
    uploadMutation.mutate({ file });
  };

  const formatTs = (ts: string | null) => {
    if (!ts) return "—";
    return new Date(ts).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  const result = data?.last_result;

  return (
    <div className="max-w-5xl w-full space-y-6">
      {/* Status card */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold text-gray-900">
            Pipeline Status
          </h3>
          <button
            type="button"
            onClick={() =>
              queryClient.invalidateQueries({ queryKey: QUERY_KEY })
            }
            disabled={isFetching}
            className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw
              className={`h-3.5 w-3.5 ${isFetching ? "animate-spin" : ""}`}
            />
            Refresh
          </button>
        </div>

        {data && (
          <dl className="space-y-3 text-sm">
            <div className="flex items-center justify-between gap-4">
              <dt className="flex items-center gap-1.5 text-gray-500">
                <Clock className="h-3.5 w-3.5" />
                CSV uploaded
              </dt>
              <dd className="font-medium text-gray-900">
                {formatTs(data.csv_last_uploaded_at)}
              </dd>
            </div>

            <div className="flex items-center justify-between gap-4">
              <dt className="text-gray-500">Task status</dt>
              <dd>
                <TaskStatusBadge status={data.last_task_status} />
              </dd>
            </div>

            {data.last_task_status === "SUCCESS" && result && (
              <div className="rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800 space-y-0.5">
                <p className="font-medium">{result.rows_added} row(s) added.</p>
                {result.invalid_loan_indexes?.length > 0 && (
                  <p className="text-xs">
                    Rows with no matching loan:{" "}
                    {result.invalid_loan_indexes.join(", ")}
                  </p>
                )}
                {result.invalid_user_indexes?.length > 0 && (
                  <p className="text-xs">
                    Rows with no matching user:{" "}
                    {result.invalid_user_indexes.join(", ")}
                  </p>
                )}
                {result.invalid_row_indexes?.length > 0 && (
                  <p className="text-xs">
                    Invalid rows: {result.invalid_row_indexes.join(", ")}
                  </p>
                )}
                {result.error && (
                  <p className="text-xs text-red-700">{result.error}</p>
                )}
              </div>
            )}

            {data.last_task_status === "FAILURE" && (
              <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                Last processing run failed. Re-upload the file to retry.
              </div>
            )}
          </dl>
        )}
      </div>

      {/* Upload card */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm space-y-4">
        <div>
          <h3 className="text-lg font-bold text-gray-900 mb-1">Upload File</h3>
          <p className="text-sm text-gray-500">Upload your CSV file.</p>
          <div className="flex flex-wrap items-center gap-3 border-t border-gray-100 pt-4">
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              disabled={isUploading}
              className="text-sm text-gray-600 file:mr-3 file:h-9 file:px-4 file:rounded-md file:border file:border-input file:bg-background file:text-sm file:font-medium file:text-gray-700 hover:file:bg-accent file:cursor-pointer disabled:opacity-50"
            />
            <button
              type="button"
              onClick={handleUpload}
              disabled={isUploading}
              className="inline-flex items-center gap-2 h-9 px-4 rounded-md border border-input bg-background text-sm font-medium text-gray-600 hover:bg-accent disabled:opacity-50"
            >
              {isUploading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Uploading…
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4" />
                  Upload
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {uploadError && (
        <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
          <span className="flex-1 break-words">{uploadError}</span>
          <button
            type="button"
            onClick={() => setUploadError("")}
            className="text-red-400 hover:text-red-600 shrink-0"
            aria-label="Dismiss error"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}
    </div>
  );
};

export default UploadDailyCollection;
